import { useRef, useState, useCallback, useEffect } from 'react';
import { WhiteboardObject } from './WhiteboardObject';
import { WhiteboardConnector } from './WhiteboardConnector';
import { useAlignmentGuides, AlignmentGuides } from './AlignmentGuides';

const GRID_SIZE = 20;
const SNAP_DISTANCE = 20; // Distance to snap to anchor points
const CONNECTOR_OFFSET = 40; // Offset for elbow connectors
const CONNECTOR_PADDING = 20; // Padding around connector bounding box

const stickyColors = {
  yellow: '#FEF3C7',
  pink: '#FCE7F3',
  blue: '#DBEAFE',
  green: '#D1FAE5',
  purple: '#EDE9FE',
  orange: '#FFEDD5',
};

// Calculate proper bounding box for connector paths
const calculateConnectorBoundingBox = (startX, startY, endX, endY, startSide, endSide, connectorType) => {
  // Start with the basic min/max
  let minX = Math.min(startX, endX);
  let minY = Math.min(startY, endY);
  let maxX = Math.max(startX, endX);
  let maxY = Math.max(startY, endY);
  
  // For elbow connectors, account for the offset segments
  if (connectorType === 'elbow' && startSide && endSide) {
    // Account for exit offset from start
    if (startSide === 'right') {
      maxX = Math.max(maxX, startX + CONNECTOR_OFFSET);
    } else if (startSide === 'left') {
      minX = Math.min(minX, startX - CONNECTOR_OFFSET);
    } else if (startSide === 'bottom') {
      maxY = Math.max(maxY, startY + CONNECTOR_OFFSET);
    } else if (startSide === 'top') {
      minY = Math.min(minY, startY - CONNECTOR_OFFSET);
    }
    
    // Account for approach offset to end
    if (endSide === 'left') {
      minX = Math.min(minX, endX - CONNECTOR_OFFSET);
    } else if (endSide === 'right') {
      maxX = Math.max(maxX, endX + CONNECTOR_OFFSET);
    } else if (endSide === 'top') {
      minY = Math.min(minY, endY - CONNECTOR_OFFSET);
    } else if (endSide === 'bottom') {
      maxY = Math.max(maxY, endY + CONNECTOR_OFFSET);
    }
  }
  
  // For curved connectors, account for control points
  if (connectorType === 'curved') {
    const distance = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
    const curvature = Math.min(distance * 0.4, 100);
    
    if (startSide === 'right') {
      maxX = Math.max(maxX, startX + curvature);
    } else if (startSide === 'left') {
      minX = Math.min(minX, startX - curvature);
    } else if (startSide === 'bottom') {
      maxY = Math.max(maxY, startY + curvature);
    } else if (startSide === 'top') {
      minY = Math.min(minY, startY - curvature);
    }
    
    if (endSide === 'left') {
      minX = Math.min(minX, endX - curvature);
    } else if (endSide === 'right') {
      maxX = Math.max(maxX, endX + curvature);
    } else if (endSide === 'top') {
      minY = Math.min(minY, endY - curvature);
    } else if (endSide === 'bottom') {
      maxY = Math.max(maxY, endY + curvature);
    }
  }
  
  return {
    x: minX - CONNECTOR_PADDING,
    y: minY - CONNECTOR_PADDING,
    width: (maxX - minX) + CONNECTOR_PADDING * 2,
    height: (maxY - minY) + CONNECTOR_PADDING * 2,
  };
};

// Get anchor points for a shape (top, right, bottom, left centers)
const getAnchorPoints = (obj) => {
  if (!obj || obj.type === 'arrow' || obj.type === 'drawing' || obj.type === 'connector') return [];
  
  return [
    { id: 'top', x: obj.x + obj.width / 2, y: obj.y, side: 'top' },
    { id: 'right', x: obj.x + obj.width, y: obj.y + obj.height / 2, side: 'right' },
    { id: 'bottom', x: obj.x + obj.width / 2, y: obj.y + obj.height, side: 'bottom' },
    { id: 'left', x: obj.x, y: obj.y + obj.height / 2, side: 'left' },
  ];
};

// Find nearest anchor point within snap distance
const findNearestAnchor = (coords, objects, excludeIds = []) => {
  let nearest = null;
  let minDist = SNAP_DISTANCE;
  
  for (const obj of objects) {
    if (excludeIds.includes(obj.id)) continue;
    
    const anchors = getAnchorPoints(obj);
    for (const anchor of anchors) {
      const dist = Math.sqrt(Math.pow(coords.x - anchor.x, 2) + Math.pow(coords.y - anchor.y, 2));
      if (dist < minDist) {
        minDist = dist;
        nearest = { ...anchor, objectId: obj.id };
      }
    }
  }
  
  return nearest;
};

// Calculate elbow path between two points
const calculateElbowPath = (start, end, startSide, endSide) => {
  const midX = (start.x + end.x) / 2;
  const midY = (start.y + end.y) / 2;
  
  // Determine the path based on anchor sides
  let points = [];
  
  if (startSide === 'right' && endSide === 'left') {
    // Horizontal first, then vertical
    points = [
      start,
      { x: midX, y: start.y },
      { x: midX, y: end.y },
      end
    ];
  } else if (startSide === 'left' && endSide === 'right') {
    points = [
      start,
      { x: midX, y: start.y },
      { x: midX, y: end.y },
      end
    ];
  } else if (startSide === 'bottom' && endSide === 'top') {
    // Vertical first, then horizontal
    points = [
      start,
      { x: start.x, y: midY },
      { x: end.x, y: midY },
      end
    ];
  } else if (startSide === 'top' && endSide === 'bottom') {
    points = [
      start,
      { x: start.x, y: midY },
      { x: end.x, y: midY },
      end
    ];
  } else if (startSide === 'right' || startSide === 'left') {
    // Start horizontal
    const offsetX = startSide === 'right' ? 40 : -40;
    points = [
      start,
      { x: start.x + offsetX, y: start.y },
      { x: start.x + offsetX, y: end.y },
      end
    ];
  } else {
    // Start vertical
    const offsetY = startSide === 'bottom' ? 40 : -40;
    points = [
      start,
      { x: start.x, y: start.y + offsetY },
      { x: end.x, y: start.y + offsetY },
      end
    ];
  }
  
  return points;
};

export const WhiteboardCanvas = ({
  objects,
  selectedIds,
  setSelectedIds,
  activeTool,
  setActiveTool,
  connectorType,
  zoom,
  setZoom,
  pan,
  setPan,
  gridType,
  snapToGrid,
  addObject,
  updateObject,
  updateObjectWithHistory,
  onContextMenu,
  onCommentClick,
  penSettings,
  shapeSettings,
  remoteCursors = {},
  remoteSelections = {},
  onCursorMove,
  whiteboardId,
  currentUserId,
  token,
  onVoteUpdate,
}) => {
  const canvasRef = useRef(null);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingPoints, setDrawingPoints] = useState([]);
  const [selectionBox, setSelectionBox] = useState(null);
  const [connectorStart, setConnectorStart] = useState(null);
  const [connectorPreview, setConnectorPreview] = useState(null);
  const [hoveredAnchor, setHoveredAnchor] = useState(null);
  const lastDrawTime = useRef(0);
  const lastDrawPos = useRef({ x: 0, y: 0 });
  const lastCursorSendTime = useRef(0);
  
  // Drag-to-draw shape state
  const [shapeDrag, setShapeDrag] = useState(null); // { startX, startY, currentX, currentY, shape }
  
  // Alignment guides - track object being dragged for snap alignment
  const [draggedObjectState, setDraggedObjectState] = useState(null);
  
  // Use alignment guides hook
  const alignmentGuides = useAlignmentGuides(
    draggedObjectState,
    objects,
    !!draggedObjectState,
    zoom
  );

  // Track spacebar for space+drag panning
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space' && !e.repeat) {
        // Prevent default only if not typing in an input
        const isTyping = document.activeElement.tagName === 'INPUT' || 
                        document.activeElement.tagName === 'TEXTAREA' ||
                        document.activeElement.isContentEditable;
        if (!isTyping) {
          e.preventDefault();
          setIsSpacePressed(true);
        }
      }
    };
    
    const handleKeyUp = (e) => {
      if (e.code === 'Space') {
        setIsSpacePressed(false);
        // If was panning with space, stop panning when space is released
        setIsPanning(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Get canvas coordinates from mouse event
  const getCanvasCoords = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left - pan.x) / zoom,
      y: (e.clientY - rect.top - pan.y) / zoom,
    };
  }, [pan, zoom]);

  // Snap position to grid
  const snapToGridPos = useCallback((pos) => {
    if (!snapToGrid) return pos;
    return {
      x: Math.round(pos.x / GRID_SIZE) * GRID_SIZE,
      y: Math.round(pos.y / GRID_SIZE) * GRID_SIZE,
    };
  }, [snapToGrid]);

  // Calculate stroke width based on drawing speed
  const calculateSpeedWidth = useCallback((currentPos, baseWidth) => {
    const now = Date.now();
    const timeDiff = now - lastDrawTime.current;
    const dx = currentPos.x - lastDrawPos.current.x;
    const dy = currentPos.y - lastDrawPos.current.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Speed = distance / time (pixels per millisecond)
    const speed = timeDiff > 0 ? distance / timeDiff : 0;
    
    // Map speed to width multiplier (faster = thinner)
    // Speed 0 (still) = 1.5x width, Speed 2+ (fast) = 0.5x width
    const multiplier = Math.max(0.4, Math.min(1.6, 1.6 - speed * 0.6));
    
    lastDrawTime.current = now;
    lastDrawPos.current = currentPos;
    
    return baseWidth * multiplier;
  }, []);

  // Handle mouse down on canvas
  const handleMouseDown = useCallback((e) => {
    // Check if click is on the canvas area (not on whiteboard objects)
    const targetTagName = e.target.tagName.toLowerCase();
    const isCanvasClick = targetTagName === 'div' || 
                          targetTagName === 'svg' || 
                          targetTagName === 'rect' ||
                          targetTagName === 'circle' ||
                          targetTagName === 'pattern';
    
    // If clicking on an object, let the object handle it (unless using hand tool or space is pressed)
    if (!isCanvasClick && e.target.closest('[data-testid^="whiteboard-object-"]') && activeTool !== 'hand' && !isSpacePressed) {
      return;
    }
    
    const coords = getCanvasCoords(e);
    const snappedCoords = snapToGridPos(coords);

    // Hand tool, middle mouse button, or space + left click for panning
    if (e.button === 1 || (e.button === 0 && (activeTool === 'hand' || isSpacePressed))) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      return;
    }

    if (e.button !== 0) return;

    // Tool-specific actions
    if (activeTool === 'select') {
      // Start selection box
      setSelectionBox({ startX: coords.x, startY: coords.y, endX: coords.x, endY: coords.y });
      setSelectedIds([]);
    } else if (activeTool.startsWith('shape-')) {
      // Start drag-to-draw shape
      const shape = activeTool.replace('shape-', '');
      setShapeDrag({
        startX: snappedCoords.x,
        startY: snappedCoords.y,
        currentX: snappedCoords.x,
        currentY: snappedCoords.y,
        shape
      });
    } else if (activeTool.startsWith('sticky-')) {
      const colorKey = activeTool.replace('sticky-', '');
      addObject('sticky', snappedCoords, { color: stickyColors[colorKey] || '#FEF3C7' });
      setActiveTool('select');
    } else if (activeTool === 'text') {
      addObject('text', snappedCoords);
      setActiveTool('select');
    } else if (activeTool === 'frame') {
      // Start drag-to-draw frame
      setShapeDrag({
        startX: snappedCoords.x,
        startY: snappedCoords.y,
        currentX: snappedCoords.x,
        currentY: snappedCoords.y,
        shape: 'frame'
      });
    } else if (activeTool === 'pen') {
      setIsDrawing(true);
      // Initialize speed tracking
      lastDrawTime.current = Date.now();
      lastDrawPos.current = coords;
      // Store initial point with base width
      const baseWidth = penSettings?.width || 2;
      setDrawingPoints([{ ...coords, width: baseWidth }]);
    } else if (activeTool === 'connector') {
      // Try to snap to nearest anchor point
      const anchor = findNearestAnchor(coords, objects);
      if (anchor) {
        setConnectorStart({ 
          x: anchor.x, 
          y: anchor.y, 
          objectId: anchor.objectId,
          side: anchor.side 
        });
      } else {
        setConnectorStart({ x: coords.x, y: coords.y, objectId: null, side: null });
      }
      setConnectorPreview({ endX: coords.x, endY: coords.y, endObjectId: null, endSide: null });
    } else if (activeTool === 'comment') {
      // Handle comment tool click
      if (onCommentClick) {
        onCommentClick(coords);
      }
    }
  }, [activeTool, getCanvasCoords, snapToGridPos, pan, addObject, setActiveTool, setSelectedIds, objects, isSpacePressed, onCommentClick, penSettings]);

  // Handle mouse move
  const handleMouseMove = useCallback((e) => {
    const coords = getCanvasCoords(e);
    
    // Send cursor position (throttled to max 20 times per second)
    if (onCursorMove) {
      const now = Date.now();
      if (now - lastCursorSendTime.current > 50) {
        lastCursorSendTime.current = now;
        onCursorMove(coords.x, coords.y);
      }
    }
    
    if (isPanning) {
      setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
      return;
    }

    const snappedCoords = snapToGridPos(coords);

    if (selectionBox) {
      setSelectionBox(prev => ({ ...prev, endX: coords.x, endY: coords.y }));
    }

    // Update shape drag preview
    if (shapeDrag) {
      setShapeDrag(prev => ({
        ...prev,
        currentX: snappedCoords.x,
        currentY: snappedCoords.y
      }));
    }

    if (isDrawing && activeTool === 'pen') {
      // Calculate width based on speed if pressure sensitive is enabled
      const baseWidth = penSettings?.width || 2;
      let pointWidth = baseWidth;
      
      if (penSettings?.pressureSensitive) {
        pointWidth = calculateSpeedWidth(coords, baseWidth);
      }
      
      setDrawingPoints(prev => [...prev, { ...coords, width: pointWidth }]);
    }

    // Show connector preview with anchor snapping
    if (connectorStart && activeTool === 'connector') {
      const excludeIds = connectorStart.objectId ? [connectorStart.objectId] : [];
      const anchor = findNearestAnchor(coords, objects, excludeIds);
      
      if (anchor) {
        setConnectorPreview({ 
          endX: anchor.x, 
          endY: anchor.y, 
          endObjectId: anchor.objectId,
          endSide: anchor.side 
        });
        setHoveredAnchor(anchor);
      } else {
        setConnectorPreview({ endX: coords.x, endY: coords.y, endObjectId: null, endSide: null });
        setHoveredAnchor(null);
      }
    }
    
    // Show anchor points when connector tool is active
    if (activeTool === 'connector' && !connectorStart) {
      const anchor = findNearestAnchor(coords, objects);
      setHoveredAnchor(anchor);
    }
  }, [isPanning, panStart, getCanvasCoords, snapToGridPos, selectionBox, isDrawing, activeTool, connectorStart, setPan, objects, shapeDrag, onCursorMove]);

  // Handle mouse up
  const handleMouseUp = useCallback((e) => {
    if (isPanning) {
      setIsPanning(false);
      return;
    }

    const coords = getCanvasCoords(e);

    // Finish shape drag-to-draw
    if (shapeDrag) {
      const minX = Math.min(shapeDrag.startX, shapeDrag.currentX);
      const minY = Math.min(shapeDrag.startY, shapeDrag.currentY);
      const width = Math.abs(shapeDrag.currentX - shapeDrag.startX);
      const height = Math.abs(shapeDrag.currentY - shapeDrag.startY);
      
      // Only create if there's a minimum size (prevents accidental tiny shapes)
      if (width > 10 || height > 10) {
        const finalWidth = Math.max(width, 40); // Minimum 40px
        const finalHeight = Math.max(height, 40);
        
        if (shapeDrag.shape === 'frame') {
          addObject('frame', { x: minX, y: minY }, {
            width: finalWidth,
            height: finalHeight
          });
        } else {
          addObject('shape', { x: minX, y: minY }, {
            shape: shapeDrag.shape,
            width: finalWidth,
            height: finalHeight
          });
        }
      } else {
        // Small click - create default sized shape
        if (shapeDrag.shape === 'frame') {
          addObject('frame', { x: shapeDrag.startX, y: shapeDrag.startY }, {
            width: 400,
            height: 300
          });
        } else {
          addObject('shape', { x: shapeDrag.startX, y: shapeDrag.startY }, {
            shape: shapeDrag.shape,
            width: 120,
            height: 80
          });
        }
      }
      
      setShapeDrag(null);
      setActiveTool('select');
      return;
    }

    // Finish connector/arrow
    if (connectorStart && activeTool === 'connector') {
      // Check for end anchor snap
      const excludeIds = connectorStart.objectId ? [connectorStart.objectId] : [];
      const endAnchor = findNearestAnchor(coords, objects, excludeIds);
      
      const endX = endAnchor ? endAnchor.x : coords.x;
      const endY = endAnchor ? endAnchor.y : coords.y;
      const endObjectId = endAnchor ? endAnchor.objectId : null;
      const endSide = endAnchor ? endAnchor.side : null;
      
      // Only create if there's some distance
      if (Math.abs(endX - connectorStart.x) > 20 || Math.abs(endY - connectorStart.y) > 20) {
        // Use the selected connector type, or default to elbow if connected to shapes
        let actualConnectorType = connectorType;
        
        // If user selected 'elbow' and shapes are connected, use elbow
        // If user selected 'straight', always use straight
        // If user selected 'curved', use curved
        if (connectorType === 'elbow' && !(connectorStart.objectId && endObjectId)) {
          // Not connected to both shapes, fall back to straight
          actualConnectorType = 'straight';
        }
        
        // Calculate proper bounding box for the connector
        const boundingBox = calculateConnectorBoundingBox(
          connectorStart.x, connectorStart.y,
          endX, endY,
          connectorStart.side, endSide,
          actualConnectorType
        );
        
        addObject('arrow', { x: boundingBox.x, y: boundingBox.y }, {
          width: boundingBox.width,
          height: boundingBox.height,
          startX: connectorStart.x,
          startY: connectorStart.y,
          endX: endX,
          endY: endY,
          startObjectId: connectorStart.objectId,
          endObjectId: endObjectId,
          startSide: connectorStart.side,
          endSide: endSide,
          connectorType: actualConnectorType,
          stroke: '#1E293B',
          strokeWidth: 2,
          arrowHead: 'end'
        });
      }
      setConnectorStart(null);
      setConnectorPreview(null);
      setHoveredAnchor(null);
    }

    // Finish selection box
    if (selectionBox) {
      const minX = Math.min(selectionBox.startX, selectionBox.endX);
      const maxX = Math.max(selectionBox.startX, selectionBox.endX);
      const minY = Math.min(selectionBox.startY, selectionBox.endY);
      const maxY = Math.max(selectionBox.startY, selectionBox.endY);

      const selectedObjs = objects.filter(obj => 
        obj.x >= minX && obj.x + obj.width <= maxX &&
        obj.y >= minY && obj.y + obj.height <= maxY
      );
      
      if (selectedObjs.length > 0) {
        setSelectedIds(selectedObjs.map(o => o.id));
      }
      
      setSelectionBox(null);
    }

    // Finish drawing
    if (isDrawing && drawingPoints.length > 2) {
      const minX = Math.min(...drawingPoints.map(p => p.x));
      const minY = Math.min(...drawingPoints.map(p => p.y));
      const maxX = Math.max(...drawingPoints.map(p => p.x));
      const maxY = Math.max(...drawingPoints.map(p => p.y));
      
      addObject('drawing', { x: minX, y: minY }, {
        width: maxX - minX,
        height: maxY - minY,
        points: drawingPoints.map(p => ({ 
          x: p.x - minX, 
          y: p.y - minY,
          width: p.width || penSettings?.width || 2
        })),
        stroke: penSettings?.color || '#1E293B',
        strokeWidth: penSettings?.width || 2,
        isHighlighter: penSettings?.isHighlighter || false,
        opacity: penSettings?.isHighlighter ? 0.4 : 1
      });
    }
    setIsDrawing(false);
    setDrawingPoints([]);
  }, [isPanning, selectionBox, objects, setSelectedIds, isDrawing, drawingPoints, addObject, connectorStart, activeTool, getCanvasCoords, penSettings]);

  // Handle wheel for zoom
  const handleWheel = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom(Math.max(0.25, Math.min(3, zoom + delta)));
    } else {
      // Pan with wheel
      setPan(prev => ({
        x: prev.x - e.deltaX,
        y: prev.y - e.deltaY,
      }));
    }
  }, [zoom, setZoom, setPan]);

  // Prevent default wheel behavior
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const preventDefault = (e) => {
      if (e.ctrlKey || e.metaKey) e.preventDefault();
    };
    
    canvas.addEventListener('wheel', preventDefault, { passive: false });
    return () => canvas.removeEventListener('wheel', preventDefault);
  }, []);

  // Render grid background
  const renderGrid = () => {
    if (gridType === 'none') return null;
    
    const gridSize = GRID_SIZE * zoom;
    
    if (gridType === 'dots') {
      return (
        <pattern
          id="dots"
          x={pan.x % gridSize}
          y={pan.y % gridSize}
          width={gridSize}
          height={gridSize}
          patternUnits="userSpaceOnUse"
        >
          <circle cx={gridSize / 2} cy={gridSize / 2} r={1} fill="#CBD5E1" />
        </pattern>
      );
    }
    
    return (
      <pattern
        id="grid"
        x={pan.x % gridSize}
        y={pan.y % gridSize}
        width={gridSize}
        height={gridSize}
        patternUnits="userSpaceOnUse"
      >
        <path
          d={`M ${gridSize} 0 L 0 0 0 ${gridSize}`}
          fill="none"
          stroke="#E2E8F0"
          strokeWidth={0.5}
        />
      </pattern>
    );
  };

  // Determine cursor style based on tool and state
  const getCursorStyle = () => {
    if (isPanning) return 'grabbing';
    if (activeTool === 'hand' || isSpacePressed) return 'grab';
    if (activeTool === 'select') return 'default';
    return 'crosshair';
  };

  return (
    <div
      ref={canvasRef}
      className="w-full h-full overflow-hidden"
      style={{ cursor: getCursorStyle() }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
      onContextMenu={(e) => onContextMenu(e, null)}
      data-testid="whiteboard-canvas"
    >
      <svg className="w-full h-full" style={{ pointerEvents: 'all' }}>
        {/* Grid pattern */}
        <defs>
          {renderGrid()}
          {/* Arrow marker */}
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill="#1E293B" />
          </marker>
          <marker
            id="arrowhead-preview"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill="#2563EB" />
          </marker>
        </defs>
        <rect 
          width="100%" 
          height="100%" 
          fill={gridType !== 'none' ? `url(#${gridType})` : '#F8FAFC'} 
          style={{ pointerEvents: 'all' }}
        />
        
        {/* Transform group for zoom and pan */}
        <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
          {/* Render connectors first (below objects) */}
          {objects.filter(o => o.type === 'connector').map(obj => (
            <WhiteboardConnector
              key={obj.id}
              connector={obj}
              objects={objects}
              isSelected={selectedIds.includes(obj.id)}
              onSelect={() => setSelectedIds([obj.id])}
            />
          ))}
          
          {/* Render objects sorted by z-index */}
          {objects
            .filter(o => o.type !== 'connector')
            .sort((a, b) => a.zIndex - b.zIndex)
            .map(obj => (
              <WhiteboardObject
                key={obj.id}
                object={obj}
                isSelected={selectedIds.includes(obj.id)}
                zoom={zoom}
                snapToGrid={snapToGrid}
                gridSize={GRID_SIZE}
                onDragStart={(dragObj) => setDraggedObjectState(dragObj)}
                onDragEnd={() => setDraggedObjectState(null)}
                alignmentGuides={alignmentGuides}
                onSelect={(e) => {
                  // Get all objects in the same group
                  const groupedIds = obj.groupId 
                    ? objects.filter(o => o.groupId === obj.groupId).map(o => o.id)
                    : [obj.id];
                  
                  if (e.shiftKey) {
                    // Shift+click: add/remove from selection
                    setSelectedIds(prev => {
                      const allSelected = groupedIds.every(id => prev.includes(id));
                      if (allSelected) {
                        // Remove all grouped objects from selection
                        return prev.filter(id => !groupedIds.includes(id));
                      } else {
                        // Add all grouped objects to selection
                        return [...new Set([...prev, ...groupedIds])];
                      }
                    });
                  } else {
                    // Regular click: select all grouped objects
                    setSelectedIds(groupedIds);
                  }
                }}
                onUpdate={(id, updates) => {
                  // If object is in a group, move all grouped objects together
                  if (obj.groupId && (updates.x !== undefined || updates.y !== undefined)) {
                    const groupedObjs = objects.filter(o => o.groupId === obj.groupId);
                    const targetObj = objects.find(o => o.id === id);
                    if (targetObj && groupedObjs.length > 1) {
                      const dx = (updates.x ?? targetObj.x) - targetObj.x;
                      const dy = (updates.y ?? targetObj.y) - targetObj.y;
                      
                      // Update all grouped objects
                      groupedObjs.forEach(gObj => {
                        updateObject(gObj.id, {
                          ...updates,
                          x: gObj.x + dx,
                          y: gObj.y + dy,
                        });
                      });
                      return;
                    }
                  }
                  updateObject(id, updates);
                }}
                onUpdateWithHistory={(id, updates) => {
                  // Same group logic for history updates
                  if (obj.groupId) {
                    const groupedObjs = objects.filter(o => o.groupId === obj.groupId);
                    groupedObjs.forEach(gObj => {
                      updateObjectWithHistory(gObj.id, {
                        x: gObj.x,
                        y: gObj.y,
                        width: gObj.width,
                        height: gObj.height,
                      });
                    });
                    return;
                  }
                  updateObjectWithHistory(id, updates);
                }}
                onContextMenu={(e) => onContextMenu(e, obj.id)}
                whiteboardId={whiteboardId}
                currentUserId={currentUserId}
                token={token}
                onVoteUpdate={onVoteUpdate}
                showVoting={obj.type === 'sticky'}
              />
            ))
          }
          
          {/* Alignment guides - rendered when dragging objects */}
          <AlignmentGuides 
            guides={alignmentGuides}
            zoom={zoom}
            pan={pan}
          />
          
          {/* Presence indicators - show who is editing each object */}
          {Object.entries(remoteSelections).map(([userId, selection]) => {
            if (!selection?.user || !selection?.selected_ids) return null;
            const userColor = selection.user.color || '#6366F1';
            const userName = selection.user.name || 'User';
            const initials = userName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
            
            return selection.selected_ids.map(objectId => {
              const obj = objects.find(o => o.id === objectId);
              if (!obj) return null;
              
              return (
                <g key={`presence-${userId}-${objectId}`} style={{ pointerEvents: 'none' }}>
                  {/* Selection border in user's color */}
                  <rect
                    x={obj.x - 3}
                    y={obj.y - 3}
                    width={obj.width + 6}
                    height={obj.height + 6}
                    fill="none"
                    stroke={userColor}
                    strokeWidth={2}
                    strokeDasharray="4 2"
                    rx={4}
                  />
                  {/* User avatar badge at top-left */}
                  <g transform={`translate(${obj.x - 12}, ${obj.y - 12})`}>
                    <circle
                      cx={0}
                      cy={0}
                      r={14}
                      fill={userColor}
                      stroke="white"
                      strokeWidth={2}
                    />
                    <text
                      x={0}
                      y={5}
                      textAnchor="middle"
                      fill="white"
                      fontSize={10}
                      fontWeight="600"
                      fontFamily="Inter, sans-serif"
                    >
                      {initials}
                    </text>
                  </g>
                </g>
              );
            });
          })}
          
          {/* Drawing preview - smooth bezier path with variable width */}
          {isDrawing && drawingPoints.length > 1 && (() => {
            const strokeColor = penSettings?.color || '#1E293B';
            const baseWidth = penSettings?.width || 2;
            const isHighlighter = penSettings?.isHighlighter || false;
            const opacity = isHighlighter ? 0.4 : 1;
            const pressureSensitive = penSettings?.pressureSensitive;
            
            // For pressure-sensitive drawing, create variable-width path segments
            if (pressureSensitive && drawingPoints.length >= 3) {
              return (
                <g opacity={opacity}>
                  {drawingPoints.slice(1).map((point, i) => {
                    const prev = drawingPoints[i];
                    const width = point.width || baseWidth;
                    return (
                      <line
                        key={i}
                        x1={prev.x}
                        y1={prev.y}
                        x2={point.x}
                        y2={point.y}
                        stroke={strokeColor}
                        strokeWidth={width}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    );
                  })}
                </g>
              );
            }
            
            // Simple preview for few points
            if (drawingPoints.length < 3) {
              return (
                <polyline
                  points={drawingPoints.map(p => `${p.x},${p.y}`).join(' ')}
                  fill="none"
                  stroke={strokeColor}
                  strokeWidth={isHighlighter ? baseWidth * 4 : baseWidth}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity={opacity}
                />
              );
            }
            
            // Create smooth path using quadratic beziers
            let pathD = `M ${drawingPoints[0].x} ${drawingPoints[0].y}`;
            
            for (let i = 1; i < drawingPoints.length - 1; i++) {
              const p1 = drawingPoints[i];
              const p2 = drawingPoints[i + 1];
              
              const midX = (p1.x + p2.x) / 2;
              const midY = (p1.y + p2.y) / 2;
              
              pathD += ` Q ${p1.x} ${p1.y}, ${midX} ${midY}`;
            }
            
            // End at last point
            const last = drawingPoints[drawingPoints.length - 1];
            pathD += ` L ${last.x} ${last.y}`;
            
            return (
              <path
                d={pathD}
                fill="none"
                stroke={strokeColor}
                strokeWidth={isHighlighter ? baseWidth * 4 : baseWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity={opacity}
              />
            );
          })()}

          {/* Connector/Arrow preview - with type support */}
          {connectorStart && connectorPreview && (() => {
            const canUseElbow = connectorStart.objectId && connectorPreview.endObjectId && 
                               connectorStart.side && connectorPreview.endSide;
            
            // Curved connector preview
            if (connectorType === 'curved') {
              const dx = connectorPreview.endX - connectorStart.x;
              const dy = connectorPreview.endY - connectorStart.y;
              const distance = Math.sqrt(dx * dx + dy * dy);
              const curvature = Math.min(distance * 0.4, 100);
              
              let cp1x, cp1y, cp2x, cp2y;
              
              if (connectorStart.side === 'right') {
                cp1x = connectorStart.x + curvature;
                cp1y = connectorStart.y;
              } else if (connectorStart.side === 'left') {
                cp1x = connectorStart.x - curvature;
                cp1y = connectorStart.y;
              } else if (connectorStart.side === 'bottom') {
                cp1x = connectorStart.x;
                cp1y = connectorStart.y + curvature;
              } else if (connectorStart.side === 'top') {
                cp1x = connectorStart.x;
                cp1y = connectorStart.y - curvature;
              } else {
                cp1x = connectorStart.x + dx * 0.25;
                cp1y = connectorStart.y;
              }
              
              if (connectorPreview.endSide === 'left') {
                cp2x = connectorPreview.endX - curvature;
                cp2y = connectorPreview.endY;
              } else if (connectorPreview.endSide === 'right') {
                cp2x = connectorPreview.endX + curvature;
                cp2y = connectorPreview.endY;
              } else if (connectorPreview.endSide === 'top') {
                cp2x = connectorPreview.endX;
                cp2y = connectorPreview.endY - curvature;
              } else if (connectorPreview.endSide === 'bottom') {
                cp2x = connectorPreview.endX;
                cp2y = connectorPreview.endY + curvature;
              } else {
                cp2x = connectorPreview.endX - dx * 0.25;
                cp2y = connectorPreview.endY;
              }
              
              const pathD = `M ${connectorStart.x} ${connectorStart.y} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${connectorPreview.endX} ${connectorPreview.endY}`;
              
              return (
                <path
                  d={pathD}
                  fill="none"
                  stroke="#2563EB"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  markerEnd="url(#arrowhead-preview)"
                />
              );
            }
            
            // Elbow connector preview - improved smart routing
            if (connectorType === 'elbow' && canUseElbow) {
              const OFFSET = 40;
              let pathD;
              
              // Calculate exit point from start shape
              let exitX = connectorStart.x;
              let exitY = connectorStart.y;
              
              if (connectorStart.side === 'right') {
                exitX = connectorStart.x + OFFSET;
              } else if (connectorStart.side === 'left') {
                exitX = connectorStart.x - OFFSET;
              } else if (connectorStart.side === 'bottom') {
                exitY = connectorStart.y + OFFSET;
              } else if (connectorStart.side === 'top') {
                exitY = connectorStart.y - OFFSET;
              }
              
              // Calculate entry approach point
              let approachX = connectorPreview.endX;
              let approachY = connectorPreview.endY;
              
              if (connectorPreview.endSide === 'left') {
                approachX = connectorPreview.endX - OFFSET;
              } else if (connectorPreview.endSide === 'right') {
                approachX = connectorPreview.endX + OFFSET;
              } else if (connectorPreview.endSide === 'top') {
                approachY = connectorPreview.endY - OFFSET;
              } else if (connectorPreview.endSide === 'bottom') {
                approachY = connectorPreview.endY + OFFSET;
              }
              
              const startHorizontal = connectorStart.side === 'left' || connectorStart.side === 'right';
              const endHorizontal = connectorPreview.endSide === 'left' || connectorPreview.endSide === 'right';
              
              if (startHorizontal && endHorizontal) {
                const midX = (exitX + approachX) / 2;
                pathD = `M ${connectorStart.x} ${connectorStart.y} L ${exitX} ${connectorStart.y} L ${midX} ${connectorStart.y} L ${midX} ${connectorPreview.endY} L ${approachX} ${connectorPreview.endY} L ${connectorPreview.endX} ${connectorPreview.endY}`;
              } else if (!startHorizontal && !endHorizontal) {
                const midY = (exitY + approachY) / 2;
                pathD = `M ${connectorStart.x} ${connectorStart.y} L ${connectorStart.x} ${exitY} L ${connectorStart.x} ${midY} L ${connectorPreview.endX} ${midY} L ${connectorPreview.endX} ${approachY} L ${connectorPreview.endX} ${connectorPreview.endY}`;
              } else if (startHorizontal && !endHorizontal) {
                pathD = `M ${connectorStart.x} ${connectorStart.y} L ${exitX} ${connectorStart.y} L ${connectorPreview.endX} ${connectorStart.y} L ${connectorPreview.endX} ${approachY} L ${connectorPreview.endX} ${connectorPreview.endY}`;
              } else {
                pathD = `M ${connectorStart.x} ${connectorStart.y} L ${connectorStart.x} ${exitY} L ${connectorStart.x} ${connectorPreview.endY} L ${approachX} ${connectorPreview.endY} L ${connectorPreview.endX} ${connectorPreview.endY}`;
              }
              
              return (
                <path
                  d={pathD}
                  fill="none"
                  stroke="#2563EB"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  markerEnd="url(#arrowhead-preview)"
                />
              );
            }
            
            // Straight connector preview (default)
            return (
              <line
                x1={connectorStart.x}
                y1={connectorStart.y}
                x2={connectorPreview.endX}
                y2={connectorPreview.endY}
                stroke="#2563EB"
                strokeWidth={2}
                strokeDasharray="5 5"
                markerEnd="url(#arrowhead-preview)"
              />
            );
          })()}

          {/* Show anchor points when connector tool is active */}
          {activeTool === 'connector' && objects
            .filter(o => o.type !== 'arrow' && o.type !== 'drawing' && o.type !== 'connector')
            .map(obj => {
              const anchors = [
                { id: 'top', x: obj.x + obj.width / 2, y: obj.y },
                { id: 'right', x: obj.x + obj.width, y: obj.y + obj.height / 2 },
                { id: 'bottom', x: obj.x + obj.width / 2, y: obj.y + obj.height },
                { id: 'left', x: obj.x, y: obj.y + obj.height / 2 },
              ];
              
              return anchors.map(anchor => (
                <circle
                  key={`${obj.id}-${anchor.id}`}
                  cx={anchor.x}
                  cy={anchor.y}
                  r={hoveredAnchor?.objectId === obj.id && hoveredAnchor?.id === anchor.id ? 6 : 4}
                  fill={hoveredAnchor?.objectId === obj.id && hoveredAnchor?.id === anchor.id ? '#2563EB' : '#94A3B8'}
                  stroke="white"
                  strokeWidth={2}
                  style={{ transition: 'all 0.15s ease' }}
                />
              ));
            })
          }

          {/* Highlight start anchor point when drawing */}
          {connectorStart && connectorStart.objectId && (
            <circle
              cx={connectorStart.x}
              cy={connectorStart.y}
              r={6}
              fill="#22C55E"
              stroke="white"
              strokeWidth={2}
            />
          )}
          
          {/* Shape drag preview */}
          {shapeDrag && (() => {
            const minX = Math.min(shapeDrag.startX, shapeDrag.currentX);
            const minY = Math.min(shapeDrag.startY, shapeDrag.currentY);
            const width = Math.abs(shapeDrag.currentX - shapeDrag.startX);
            const height = Math.abs(shapeDrag.currentY - shapeDrag.startY);
            const fillColor = shapeSettings?.fillColor || '#FFFFFF';
            const strokeColor = shapeSettings?.strokeColor || '#1E293B';
            const strokeWidth = shapeSettings?.strokeWidth || 2;
            const fillOpacity = shapeSettings?.fillOpacity ?? 1;
            
            // Render shape preview based on type
            const renderShapePreview = () => {
              switch (shapeDrag.shape) {
                case 'circle':
                  return (
                    <ellipse
                      cx={minX + width / 2}
                      cy={minY + height / 2}
                      rx={width / 2}
                      ry={height / 2}
                      fill={fillColor}
                      fillOpacity={fillOpacity}
                      stroke={strokeColor}
                      strokeWidth={strokeWidth}
                      strokeDasharray="5 5"
                    />
                  );
                case 'diamond':
                  return (
                    <polygon
                      points={`${minX + width/2},${minY} ${minX + width},${minY + height/2} ${minX + width/2},${minY + height} ${minX},${minY + height/2}`}
                      fill={fillColor}
                      fillOpacity={fillOpacity}
                      stroke={strokeColor}
                      strokeWidth={strokeWidth}
                      strokeDasharray="5 5"
                    />
                  );
                case 'rounded':
                  return (
                    <rect
                      x={minX}
                      y={minY}
                      width={width}
                      height={height}
                      rx={12}
                      fill={fillColor}
                      fillOpacity={fillOpacity}
                      stroke={strokeColor}
                      strokeWidth={strokeWidth}
                      strokeDasharray="5 5"
                    />
                  );
                case 'frame':
                  return (
                    <rect
                      x={minX}
                      y={minY}
                      width={width}
                      height={height}
                      fill="transparent"
                      stroke="#94A3B8"
                      strokeWidth={1}
                      strokeDasharray="5 5"
                    />
                  );
                default:
                  return (
                    <rect
                      x={minX}
                      y={minY}
                      width={width}
                      height={height}
                      fill={fillColor}
                      fillOpacity={fillOpacity}
                      stroke={strokeColor}
                      strokeWidth={strokeWidth}
                      strokeDasharray="5 5"
                    />
                  );
              }
            };
            
            return (
              <>
                {renderShapePreview()}
                {/* Size indicator */}
                <text
                  x={minX + width / 2}
                  y={minY + height + 20}
                  textAnchor="middle"
                  fill="#64748B"
                  fontSize={12}
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {Math.round(width)} × {Math.round(height)}
                </text>
              </>
            );
          })()}
          
          {/* Remote cursors */}
          {Object.entries(remoteCursors).map(([userId, cursor]) => {
            if (!cursor || !cursor.user) return null;
            const cursorColor = cursor.user.color || '#6366F1';
            return (
              <g key={userId} style={{ pointerEvents: 'none' }}>
                {/* Cursor pointer */}
                <path
                  d={`M ${cursor.x} ${cursor.y} L ${cursor.x} ${cursor.y + 18} L ${cursor.x + 5} ${cursor.y + 14} L ${cursor.x + 10} ${cursor.y + 20} L ${cursor.x + 12} ${cursor.y + 18} L ${cursor.x + 7} ${cursor.y + 12} L ${cursor.x + 13} ${cursor.y + 12} Z`}
                  fill={cursorColor}
                  stroke="white"
                  strokeWidth={1}
                />
                {/* Name label */}
                <rect
                  x={cursor.x + 15}
                  y={cursor.y + 16}
                  width={Math.max(50, (cursor.user.name?.length || 5) * 7 + 12)}
                  height={20}
                  rx={4}
                  fill={cursorColor}
                />
                <text
                  x={cursor.x + 21}
                  y={cursor.y + 30}
                  fill="white"
                  fontSize={11}
                  fontFamily="Inter, sans-serif"
                  fontWeight="500"
                >
                  {cursor.user.name || 'Anonymous'}
                </text>
              </g>
            );
          })}
        </g>
        
        {/* Selection box (in screen coordinates) */}
        {selectionBox && (
          <rect
            x={Math.min(selectionBox.startX, selectionBox.endX) * zoom + pan.x}
            y={Math.min(selectionBox.startY, selectionBox.endY) * zoom + pan.y}
            width={Math.abs(selectionBox.endX - selectionBox.startX) * zoom}
            height={Math.abs(selectionBox.endY - selectionBox.startY) * zoom}
            fill="rgba(37, 99, 235, 0.1)"
            stroke="#2563EB"
            strokeWidth={1}
            strokeDasharray="4 4"
          />
        )}
        
        {/* Multi-selection bounding box */}
        {selectedIds.length > 1 && (() => {
          const selectedObjs = objects.filter(obj => selectedIds.includes(obj.id));
          if (selectedObjs.length < 2) return null;
          
          const minX = Math.min(...selectedObjs.map(o => o.x));
          const minY = Math.min(...selectedObjs.map(o => o.y));
          const maxX = Math.max(...selectedObjs.map(o => o.x + (o.width || 0)));
          const maxY = Math.max(...selectedObjs.map(o => o.y + (o.height || 0)));
          
          // Check if all selected objects are in the same group
          const groupIds = [...new Set(selectedObjs.map(o => o.groupId).filter(Boolean))];
          const isGrouped = groupIds.length === 1 && selectedObjs.every(o => o.groupId === groupIds[0]);
          
          return (
            <g>
              {/* Bounding box */}
              <rect
                x={minX * zoom + pan.x - 4}
                y={minY * zoom + pan.y - 4}
                width={(maxX - minX) * zoom + 8}
                height={(maxY - minY) * zoom + 8}
                fill="none"
                stroke={isGrouped ? "#8B5CF6" : "#2563EB"}
                strokeWidth={2}
                strokeDasharray={isGrouped ? "none" : "6 3"}
                rx={4}
              />
              
              {/* Group indicator badge */}
              {isGrouped && (
                <g>
                  <rect
                    x={minX * zoom + pan.x - 4}
                    y={minY * zoom + pan.y - 24}
                    width={60}
                    height={18}
                    rx={4}
                    fill="#8B5CF6"
                  />
                  <text
                    x={minX * zoom + pan.x + 26}
                    y={minY * zoom + pan.y - 11}
                    textAnchor="middle"
                    fill="white"
                    fontSize={10}
                    fontFamily="Inter, sans-serif"
                    fontWeight="600"
                  >
                    GROUPED
                  </text>
                </g>
              )}
              
              {/* Selection count badge */}
              <rect
                x={maxX * zoom + pan.x - 20}
                y={minY * zoom + pan.y - 24}
                width={28}
                height={18}
                rx={9}
                fill={isGrouped ? "#8B5CF6" : "#2563EB"}
              />
              <text
                x={maxX * zoom + pan.x - 6}
                y={minY * zoom + pan.y - 11}
                textAnchor="middle"
                fill="white"
                fontSize={11}
                fontFamily="Inter, sans-serif"
                fontWeight="600"
              >
                {selectedObjs.length}
              </text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
};
